import React, { useCallback } from 'react';
import { Input, Box, Text } from '@mafc/trolly';
import { useSelector, useDispatch } from 'react-redux';
import { IAppState, COLORS, TYPOGRAPHY } from '@mafc/common';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getErrorMsg, FormErrorTypes } from 'utils/form.util';
import { emailRegEx } from 'pages/Checkout/constants/regexps';
import { storePaymentDetails } from 'pages/Checkout/redux/actions/payment.action';
import EPaymentMethods from '../../PamentMethods.enum';
import { ChangeEvent } from 'react';
import { useEffectOnce } from '@mafc/hooks';

const KEN_REGEX = /(\+254)(1|7|01|07)[0-9]{8,9}$/;

const Mpesa = () => {
  const { email } = useSelector((state: IAppState) => state.userData);
  const { register, errors, getValues } = useForm({
    mode: 'onChange',
    defaultValues: {
      email,
      phone: '+254',
    },
  });
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const allValues = getValues();
  const formUpdate = useCallback(
    (details: { email?: string; phone?: string } = {}) => {
      const values = { ...allValues, ...details };
      const isValid = isValidDetails(values);
      const { email: emailAddress, phone: mobileNumber } = values;
      dispatch(
        storePaymentDetails(EPaymentMethods.MOBILE_WALLET, {
          isValid,
          easyPaisaDetails: { emailAddress, mobileNumber: encodeURIComponent(mobileNumber || '') },
        })
      );
    },
    [allValues, dispatch]
  );

  const isValidDetails = ({ email: emailEx = '', phone: phoneEx = '' }) => {
    return emailRegEx.test(emailEx) && KEN_REGEX.test(phoneEx);
  };

  const handleFieldChange = (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
    formUpdate({ [key]: e.target.value });
  };

  useEffectOnce(() => {
    formUpdate();
  });

  return (
    <Box p={20} flexDirection="column">
      <Text>Please provide your mpesa details</Text>
      <Box display={['block', 'flex']} pt={20}>
        <Box mr={[null, '20px']} minWidth="200px">
          <Input
            label={t('easyPaisa.emailAddress')}
            overrideInternalOptions={{
              used: !!email,
            }}
            name="email"
            register={register({
              required: getErrorMsg(FormErrorTypes.required, t),
              maxLength: {
                value: 255,
                message: getErrorMsg(FormErrorTypes.maxLength, t, 255),
              },
              pattern: {
                value: emailRegEx,
                message: getErrorMsg(FormErrorTypes.pattern, t, 'email'),
              },
            })}
            onChange={handleFieldChange('email')}
            successIcon={true}
            data-testid="__checkout_delivery_mpesa_email"
            autoComplete="email"
          />
          {errors.email && (
            <Text.Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
              {errors.email.message}
            </Text.Paragraph>
          )}
        </Box>
        <Box minWidth="200px">
          <Input
            overrideInternalOptions={{
              used: !!allValues.phone,
            }}
            label={t('addressForm.mobileNumber')}
            name={'phone'}
            register={register({
              required: getErrorMsg(FormErrorTypes.required, t),
              pattern: {
                value: KEN_REGEX,
                message: getErrorMsg(
                  FormErrorTypes.pattern,
                  t,
                  t('addressForm.mobileNumber').toLowerCase()
                ),
              },
            })}
            onChange={handleFieldChange('phone')}
            successIcon={true}
            data-testid="__checkout_delivery_mpesa_phone"
            autoComplete="tel"
          />
          {errors.phone && (
            <Text.Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
              {errors.phone.message}
            </Text.Paragraph>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Mpesa;
